import * as React from "react";
import { SearchBar } from "Components/General/SearchBar";
import { DocumentsSearchForm } from "./DocumentsSearchForm";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { WithTranslation, withTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { projectsDataStore } from "Stores";
import { documentsSearchStore } from "../../Stores/DocumentsSearchStore";
import { RouterStore } from "Config/CustomBrowserRouter";
import { getUrlParams } from "utils/getUrlParams";
import { documentsTableStore } from "../../Stores/DocumentsTableStore";
import { modalProps } from "Components/FilePrint/utils/modalProps";

interface DocumentsSearchbarProps extends WithTranslation {
  disabled?: boolean; // to disable radio buttons that use folderId in search query
  forbidEmptySearchString?: boolean;
}

export const DocumentsSearchbar = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<DocumentsSearchbarProps> {
      private searchbarRef: React.RefObject<React.Component> = React.createRef();

      updateSearchbarValue = () => {
        if (this.searchbarRef.current) {
          this.searchbarRef.current.forceUpdate();
        }
      };

      handleRadioChange = (value) => {
        documentsSearchStore.setRadioValue(value);
      };

      handleSearch = async (value: string) => {
        if (value?.length) {
          try {
            const requestBody: API_DocSearchReq = { fileName: value };
            const searchFolderId = this.searchFolderId || documentsTableStore.currentFolderId;
            requestBody.folderId = searchFolderId;
            requestBody.returnDeleted = documentsTableStore.showDeleted;
            RouterStore.push(
              `/project/${projectsDataStore.projectId}/documents/docs/search-results?folderId=${searchFolderId}`
            );
            await documentsSearchStore.submitSearch(requestBody);
          } catch (error) {
            throw error;
          }
        } else {
          const { folderId } = getUrlParams(RouterStore.location.search);
          documentsSearchStore.reset();
          if (folderId) {
            documentsTableStore.goToFolder(+folderId);
          } else {
            RouterStore.push(`/project/${projectsDataStore.projectId}/documents/docs`);
          }
        }
      };

      get searchFolderId() {
        const { folderId } = getUrlParams(window.location.search);
        return +folderId;
      }

      get selectOptions() {
        return {
          defaultValue: documentsSearchStore.radioValue,
          onChange: this.handleRadioChange,
          options: [
            { title: this.props.t("From current directory"), value: "CURR_CHILDREN", disabled: this.props.disabled },
            { title: this.props.t("Only current directory"), value: "CURR_DIR", disabled: this.props.disabled },
            { title: this.props.t("Whole project"), value: "ALL" },
          ],
        };
      }

      render() {
        const { t } = this.props;

        return (
          <>
            <ModalConsumer>
              {({ showModal }) => (
                <SearchBar
                  ref={this.searchbarRef}
                  onSearch={this.handleSearch}
                  placeholder={t("Search in filename")}
                  queries={true}
                  hideClear={true}
                  forbidEmptySearchString={this.props.forbidEmptySearchString}
                  onShowSearchModal={() =>
                    showModal(DocumentsSearchForm, {
                      modalProps: {
                        ...modalProps,
                        width: 1000,
                        title: t("File detail search"),
                        wrapClassName: "email-send-modal doc-search-form",
                      },
                      componentProps: {
                        initialData: {
                          returnDeleted: documentsTableStore.showDeleted,
                          ...documentsSearchStore.searchReqQueries,
                        },
                        disabled: this.props.disabled,
                        updateSearchbarValue: this.updateSearchbarValue,
                      },
                    })
                  }
                  style={{ marginBottom: 0 }}
                  select={this.selectOptions}
                  searchIconTooltip={t("Search in filename")}
                  settingsIconTooltip={t("File detail search")}
                  defaultValue={documentsSearchStore.searchReqQueries.fileName}
                />
              )}
            </ModalConsumer>
          </>
        );
      }
    }
  )
);
